import React, {
  CSSProperties,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import Tracker from '../utils/tracking';
import Launcher from '../components/launcher/Launcher';
import { CurrencyContextProvider } from '../contexts/CurrencyContext';
import { OffersContextProvider } from '../contexts/OffersContext';
import { CartContextProvider } from '../contexts/CartContext';
import { ProductsContextProvider } from '../contexts/ProductsContext';
import useSessionStorage from '../utils/useSessionStorage';
import { PreviewContextProvider } from '../contexts/PreviewContext';
import { getAddedOffers } from '../core/products';
import { isCartPage } from '../dom';
import { useOffers } from '../hooks/useOffers';
import { useAnimatedUnmount } from '../hooks/useDelayUnmount';
import { ScreenType } from '../domain';
import { Boundary } from '../utils/ErrorBoundary';
import { AnimationContextProvider } from '../contexts/AnimationContext';
import isMobile from '../common/utils/isMobile';
import {
  TranslationsProvider,
  useTranslations,
} from '../contexts/translations/TranslationsContext';
import {
  OfferTranslationsProvider,
  useOfferTranslations,
} from '../contexts/translations/OfferTranslationsContext';
import { CacheProvider } from '../contexts/translations/CacheContext';
import {
  buildTranslations,
  getGeneralMetaobject,
} from '../contexts/translations/utils/getTranslations';
import { prefetchOfferTranslations } from '../contexts/translations/utils/cacheUtils';

const AppBody = React.lazy(() => import('./AppBody'));

type AppProps = {
  storefrontAccessToken: string;
};

const App = ({ storefrontAccessToken }: AppProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [currentScreen, setCurrentScreen] = useState<ScreenType>(
    ScreenType.OFFERS,
  );
  const [addedOffers, setAddedOffers] = useState(() => getAddedOffers());
  const { setTranslations, translations } = useTranslations();
  const { cacheTranslations } = useOfferTranslations();
  const delay = 250;
  const mountedStyle: CSSProperties = useMemo(
    () => ({
      opacity: '1',
      transition: `opacity 150ms`,
      transitionDelay: `${delay}ms`,
      transitionTimingFunction: `cubic-bezier(0, 0, 0.58, 1)`,
    }),
    [delay],
  );
  const unmountedStyle: CSSProperties = useMemo(
    () => ({
      opacity: 0,
      transition: `opacity 100ms`,
      transitionTimingFunction: `cubic-bezier(0, 0, 0.58, 1)`,
    }),
    [delay],
  );

  const { showComponent, style } = useAnimatedUnmount(
    open,
    delay,
    mountedStyle,
    unmountedStyle,
  );

  const toggle = () => {
    setCurrentScreen(ScreenType.OFFERS);
    setOpen(!open);
  };

  const [discountCode] = useSessionStorage('giftbox-discount', '');

  const tracker = new Tracker();

  const [{ offers, settings, remainingOffers }, updateCartCallback] = useOffers(
    discountCode,
    setOpen,
    tracker,
  );

  useEffect(() => {
    if (!Object.keys(settings).length) {
      // !isEmpty(settings)
      return;
    }

    // We're initializing the translations here because we need to have the `settings` ready.
    const loadTranslations = async () => {
      const generalMetaobject = await getGeneralMetaobject(
        storefrontAccessToken,
      );
      const translations = buildTranslations(generalMetaobject, settings);

      setTranslations(translations);

      if (!offers || offers.length === 0) {
        return;
      }

      await prefetchOfferTranslations(
        storefrontAccessToken ?? '',
        offers,
        generalMetaobject,
        settings,
        cacheTranslations,
      );
    };

    loadTranslations();
  }, [storefrontAccessToken, settings, offers, cacheTranslations]);

  if (
    !offers ||
    !settings ||
    !Object.keys(settings).length ||
    offers.length === 0
  ) {
    return null;
  }

  const theme = {
    primaryColor: settings.primary_color,
    backgroundColor: settings.background_color,
    alignLeft: settings.align_left,
    sideSpacing: settings.side_spacing,
    bottomSpacing: settings.bottom_spacing,
  };

  const CustomCss =
    createGlobalStyle`${settings.custom_css}` as React.FunctionComponent;

  const hasUnselectedOffers = addedOffers.find(
    (addedOffer) =>
      addedOffer.type === 'MultipleProductOffer' &&
      (!addedOffer?.productHandle || addedOffer?.productHandle === 'undefined'),
  );

  useEffect(() => {
    const handleUpdateOffers = () => setAddedOffers(getAddedOffers());

    window.addEventListener('offersUpdated', handleUpdateOffers);

    return () => {
      window.removeEventListener('offersUpdated', handleUpdateOffers);
    };
  }, []);

  const checkUnselectedOffers = () => {
    const hasUnselectedOffersRef = useRef<boolean | null>(null);
    if (
      hasUnselectedOffersRef.current !== !!hasUnselectedOffers &&
      hasUnselectedOffers &&
      (isCartPage() || window.GIFTBOX_FORCE_AUTO_OPEN)
    ) {
      setOpen(true);
    }
    hasUnselectedOffersRef.current = !!hasUnselectedOffers;
  };
  checkUnselectedOffers();

  const AppWithBoundary = () => (
    <Boundary>
      <Suspense fallback={<div>Loading</div>}>
        <AppBody
          offers={offers}
          settings={settings}
          updateCartCallback={updateCartCallback}
          currentScreen={currentScreen}
          open={showComponent}
          toggle={toggle}
          setCurrentScreen={setCurrentScreen}
          CustomCss={CustomCss}
          style={isMobile() ? {} : style}
        />
      </Suspense>
    </Boundary>
  );

  return (
    <OffersContextProvider offers={offers}>
      {open && offers.find((o) => o.justAdded) && (
        <div
          onClick={toggle}
          className="GiftBox-Backdrop fixed top-0 left-0 bg-black bg-opacity-60 right-0 bottom-0 hidden"
        >
          <p> </p> {/* Avoiding global :empty selector */}
        </div>
      )}

      <ThemeProvider theme={theme}>
        {/**
            Mobile: we dont use showComponent because it doesnt allow animations.
              Everything is handled by react-modal-sheet library.
            Desktop: we need to use showComponent because, by using only opacity animation to hide the popup,
              we are not able to click on the elements underneath the popup.
          **/}
        {isMobile() ? AppWithBoundary() : null}
        {!isMobile() && showComponent ? AppWithBoundary() : null}
      </ThemeProvider>

      <Launcher
        theme={theme}
        remainingOffers={remainingOffers}
        tracker={tracker}
        open={open}
        toggle={toggle}
        offers={offers}
        settings={settings}
        hasUnselectedOffers={hasUnselectedOffers}
      />
      <CustomCss />
    </OffersContextProvider>
  );
};

type Props = {
  storefrontAccessToken: string;
};

export default ({ storefrontAccessToken }: Props) => (
  <div className="fixed w-0 h-0 bottom-0 right-0 z-50">
    <TranslationsProvider storefrontAccessToken={storefrontAccessToken}>
      <CacheProvider>
        <OfferTranslationsProvider>
          <PreviewContextProvider>
            <CartContextProvider>
              <ProductsContextProvider
                storefrontAccessToken={storefrontAccessToken}
              >
                <CurrencyContextProvider>
                  <AnimationContextProvider>
                    <App storefrontAccessToken={storefrontAccessToken} />
                  </AnimationContextProvider>
                </CurrencyContextProvider>
              </ProductsContextProvider>
            </CartContextProvider>
          </PreviewContextProvider>
        </OfferTranslationsProvider>
      </CacheProvider>
    </TranslationsProvider>
  </div>
);
